import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getConfig } from '../runtimeConfig';
import { cachedFetch } from '../cacheManager';
import ExcelTemplateBuilder from './ExcelTemplateBuilder';

const API = getConfig().REACT_APP_API;

// ============ INTERFACES ============
interface EmailRecipient {
  id: string;
  email: string;
  name: string;
  role: 'primary' | 'cc' | 'bcc';
}

interface ScheduleConfig {
  id: string;
  name: string;
  type: 'client' | 'warehouse' | 'group';
  targetId: string;
  targetName: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string; // HH:MM format
  weekDay?: number; // 0-6 for weekly
  monthDay?: number; // 1-31 for monthly
  recipients: EmailRecipient[];
  includeExpiry: boolean;
  includeStock: boolean;
  includePallets: boolean;
  includeBins: boolean;
  includeActivity: boolean;
  includeCharts: boolean;
  attachExcel: boolean;
  isActive: boolean;
  lastRun?: string;
  nextRun?: string;
  createdAt: string;
}

interface CustomerGroup {
  id: string;
  name: string;
  color: string;
  customers: string[];
  createdAt?: string;
}

interface Props {
  customerStorageData?: any[];
  warehouseData?: any[];
  stats?: any;
  virtualStats?: any;
  expiryStats?: any;
}

// ============ MAIN COMPONENT ============
const InventoryAutoMailScheduler: React.FC<Props> = ({
  customerStorageData = [],
  warehouseData = [],
  stats,
  virtualStats,
  expiryStats
}) => {
  // ============ STATE ============
  const [schedules, setSchedules] = useState<ScheduleConfig[]>([]);
  const [customerList, setCustomerList] = useState<{ code: string; name: string }[]>([]);
  const [warehouseList, setWarehouseList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'schedules' | 'create' | 'groups' | 'preview' | 'history' | 'templates'>('schedules');
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleConfig | null>(null);
  const [emailHistory, setEmailHistory] = useState<any[]>([]);

  // Form state for creating new schedule
  const [formData, setFormData] = useState<Partial<ScheduleConfig> & { useGroup?: boolean; groupId?: string }>({
    name: '',
    type: 'client',
    targetId: '',
    targetName: '',
    frequency: 'daily',
    time: '08:00',
    weekDay: 1,
    monthDay: 1,
    recipients: [],
    includeExpiry: true,
    includeStock: true,
    includePallets: true,
    includeBins: true,
    includeActivity: true,
    includeCharts: true,
    attachExcel: true,
    isActive: true,
    useGroup: false,
    groupId: ''
  });

  // New recipient form
  const [newRecipient, setNewRecipient] = useState<{ email: string; name: string; role: 'primary' | 'cc' | 'bcc' }>({ email: '', name: '', role: 'primary' });

  // Customer Groups state
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CustomerGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', color: '#3b82f6', customers: [] as string[] });
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  // ============ DATA LOADING ============
  const fetchSchedules = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await cachedFetch(`${API}/inventory-mail/schedules`, { ttl: 60000 });
      if (data.success) {
        // Map backend response to frontend interface
        const mappedSchedules: ScheduleConfig[] = (data.data || []).map((s: any) => ({
          id: s.id?.toString() || '',
          name: s.name || '',
          type: s.type || 'client',
          targetId: s.targetId || '',
          targetName: s.targetName || s.targetId || '',
          frequency: s.frequency || 'daily',
          time: s.time || '08:00',
          weekDay: s.dayOfWeek ?? 1,
          monthDay: s.dayOfMonth ?? 1,
          // Convert email strings to EmailRecipient objects
          recipients: Array.isArray(s.recipients)
            ? s.recipients.map((emailOrObj: string | { email?: string; name?: string }, idx: number) => ({
                id: `${s.id}-${idx}`,
                email: typeof emailOrObj === 'string' ? emailOrObj : (emailOrObj?.email || ''),
                name: typeof emailOrObj === 'string' ? emailOrObj.split('@')[0] : (emailOrObj?.name || ''),
                role: (idx === 0 ? 'primary' : 'cc') as 'primary' | 'cc' | 'bcc'
              }))
            : [],
          includeExpiry: true,
          includeStock: true,
          includePallets: true,
          includeBins: true,
          includeActivity: true,
          includeCharts: true,
          attachExcel: s.attachExcel !== false,
          isActive: s.isActive !== false,
          lastRun: s.lastRun || undefined,
          nextRun: s.nextRun || undefined,
          createdAt: s.createdAt || new Date().toISOString()
        }));
        setSchedules(mappedSchedules);
        console.log(`✅ Loaded ${mappedSchedules.length} schedules from API`);
      }
    } catch (e: any) {
      console.error('❌ Failed to fetch schedules:', e.message);
      setSchedules([]);
    }
    setIsLoading(false);
  }, []);

  const fetchCustomers = useCallback(async () => {
    // Try API first, then fall back to props
    try {
      const data = await cachedFetch(`${API}/warehouse/filters`, { ttl: 600000 });
      if (data.success && data.data.customers && data.data.customers.length > 0) {
        setCustomerList(data.data.customers);
        console.log(`✅ Loaded ${data.data.customers.length} customers from API`);
        return;
      }
    } catch (e) {
      console.log('⚠️ API not available, trying props...');
    }
    
    // Fallback to props
    if (customerStorageData && customerStorageData.length > 0) {
      const seen = new Set<string>();
      const list: { code: string; name: string }[] = [];
      customerStorageData.forEach((item: any) => {
        const customerCode = item.CUSTOMER || item.Customer || item.BMFIRM;
        if (customerCode && !seen.has(customerCode)) {
          seen.add(customerCode);
          list.push({ 
            code: customerCode, 
            name: item.CUSTOMER_NAME || item.CustomerName || customerCode 
          });
        }
      });
      if (list.length > 0) {
        setCustomerList(list.sort((a, b) => a.code.localeCompare(b.code)));
        console.log(`✅ Loaded ${list.length} customers from props`);
      }
    }
  }, [customerStorageData]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API}/warehouse/filters`, { ttl: 600000 });
      if (data.success && data.data.warehouses) {
        setWarehouseList(data.data.warehouses);
      }
    } catch (e) {
      // Extract from warehouseData
      const warehouses = Array.from(new Set(warehouseData.map((w: any) => w.WAREHOUSE))).filter(Boolean);
      setWarehouseList(warehouses as string[]);
    }
  }, [warehouseData]);

  const fetchEmailHistory = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API}/inventory-mail/history`, { ttl: 60000 });
      if (data.success) {
        setEmailHistory(data.data || []);
        console.log('✅ Loaded email history from API');
      }
    } catch (e: any) {
      // Silently use empty history - backend not deployed yet
      if (e.response?.status === 404) {
        console.log('📋 History API not available - using local storage');
      }
      setEmailHistory([]);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API}/customer-groups`, { ttl: 300000 });
      if (data.success) {
        setCustomerGroups(data.data || []);
        console.log(`✅ Loaded ${data.data?.length || 0} customer groups from API`);
      }
    } catch (e) {
      console.log('⚠️ Customer groups API not available, using localStorage fallback');
      const stored = localStorage.getItem('customerGroups');
      if (stored) {
        try {
          setCustomerGroups(JSON.parse(stored));
        } catch { setCustomerGroups([]); }
      }
    }
  }, []);

  useEffect(() => {
    // Run all fetches in parallel for faster loading
    Promise.all([
      fetchSchedules(),
      fetchCustomers(),
      fetchWarehouses(),
      fetchEmailHistory(),
      fetchGroups()
    ]).catch(console.error);
  }, [fetchSchedules, fetchCustomers, fetchWarehouses, fetchEmailHistory, fetchGroups]);

  // Re-fetch customers when customerStorageData prop updates
  useEffect(() => {
    if (customerStorageData && customerStorageData.length > 0) {
      console.log('📦 customerStorageData available, extracting customers...');
      fetchCustomers();
    }
  }, [customerStorageData, fetchCustomers]);
  
  // Re-fetch warehouses when warehouseData prop updates
  useEffect(() => {
    if (warehouseData && warehouseData.length > 0) {
      console.log('🏭 warehouseData available, extracting warehouses...');
      fetchWarehouses();
    }
  }, [warehouseData, fetchWarehouses]);

  // ============ HANDLERS ============
  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const addRecipient = () => {
    if (!newRecipient.email || !newRecipient.name) {
      showMessage('error', '⚠️ Please enter email and name');
      return;
    }
    if ((formData.recipients?.length || 0) >= 5) {
      showMessage('error', '⚠️ Maximum 5 recipients allowed');
      return;
    }
    const recipient: EmailRecipient = {
      id: Date.now().toString(),
      ...newRecipient
    };
    setFormData({
      ...formData,
      recipients: [...(formData.recipients || []), recipient]
    });
    setNewRecipient({ email: '', name: '', role: 'primary' });
  };

  const removeRecipient = (id: string) => {
    setFormData({
      ...formData,
      recipients: formData.recipients?.filter(r => r.id !== id) || []
    });
  };

  const [isCreating, setIsCreating] = useState(false);

  const createSchedule = async () => {
    console.log('🚀 Create Schedule clicked');
    console.log('📋 Form data:', formData);

    // Validate based on whether using group or single customer
    if (formData.useGroup) {
      if (!formData.name || !formData.groupId || !formData.recipients?.length) {
        showMessage('error', '⚠️ Please fill all required fields and add at least one recipient');
        console.log('❌ Validation failed - missing fields');
        return;
      }
    } else {
      if (!formData.name || !formData.targetId || !formData.recipients?.length) {
        showMessage('error', '⚠️ Please fill all required fields and add at least one recipient');
        console.log('❌ Validation failed - missing fields');
        return;
      }
    }

    setIsCreating(true);
    try {
      // If using group, create ONE schedule with multiple customers (comma-separated)
      if (formData.useGroup && formData.groupId) {
        const group = customerGroups.find(g => g.id === formData.groupId);
        if (!group || group.customers.length === 0) {
          showMessage('error', '⚠️ Selected group has no customers');
          setIsCreating(false);
          return;
        }

        // Create single schedule with all customers as comma-separated targetId
        const schedule: ScheduleConfig = {
          id: Date.now().toString(),
          name: formData.name!,
          type: 'group', // Special type for group reports
          targetId: group.customers.join(','), // All customer codes comma-separated
          targetName: group.name, // Group name as the report name
          frequency: formData.frequency!,
          time: formData.time!,
          weekDay: formData.weekDay,
          monthDay: formData.monthDay,
          recipients: formData.recipients!,
          includeExpiry: formData.includeExpiry!,
          includeStock: formData.includeStock!,
          includePallets: formData.includePallets!,
          includeBins: formData.includeBins!,
          includeActivity: formData.includeActivity!,
          includeCharts: formData.includeCharts!,
          attachExcel: formData.attachExcel!,
          isActive: formData.isActive!,
          createdAt: new Date().toISOString(),
          nextRun: calculateNextRun(formData.frequency!, formData.time!, formData.weekDay, formData.monthDay)
        };

        const apiPayload = {
          name: schedule.name,
          type: 'group',
          targetId: schedule.targetId, // Comma-separated customer codes
          targetName: schedule.targetName,
          frequency: schedule.frequency,
          time: schedule.time,
          dayOfWeek: schedule.weekDay,
          dayOfMonth: schedule.monthDay,
          attachExcel: schedule.attachExcel,
          recipients: schedule.recipients.map(r => r.email)
        };

        try {
          const res = await axios.post(`${API}/inventory-mail/schedules`, apiPayload);
          if (res.data.success) {
            setSchedules([...schedules, { ...schedule, id: res.data.data?.id?.toString() || schedule.id }]);
            showMessage('success', `✅ Group report "${group.name}" with ${group.customers.length} customers created!`);
            resetForm();
            setActiveTab('schedules');
            fetchSchedules();
          } else {
            showMessage('error', `❌ Failed: ${res.data.error || 'Unknown error'}`);
          }
        } catch (e: any) {
          showMessage('error', `❌ Error: ${e.response?.data?.error || e.message || 'Connection failed'}`);
        }
      } else {
        // Single customer schedule
        const schedule: ScheduleConfig = {
          id: Date.now().toString(),
          name: formData.name!,
          type: formData.type!,
          targetId: formData.targetId!,
          targetName: formData.type === 'client'
            ? customerList.find(c => c.code === formData.targetId)?.name || formData.targetId!
            : formData.targetId!,
          frequency: formData.frequency!,
          time: formData.time!,
          weekDay: formData.weekDay,
          monthDay: formData.monthDay,
          recipients: formData.recipients!,
          includeExpiry: formData.includeExpiry!,
          includeStock: formData.includeStock!,
          includePallets: formData.includePallets!,
          includeBins: formData.includeBins!,
          includeActivity: formData.includeActivity!,
          includeCharts: formData.includeCharts!,
          attachExcel: formData.attachExcel!,
          isActive: formData.isActive!,
          createdAt: new Date().toISOString(),
          nextRun: calculateNextRun(formData.frequency!, formData.time!, formData.weekDay, formData.monthDay)
        };

        // Map frontend field names to backend expected names
        const apiPayload = {
          name: schedule.name,
          type: schedule.type,
          targetId: schedule.targetId,
          targetName: schedule.targetName,
          frequency: schedule.frequency,
          time: schedule.time,
          dayOfWeek: schedule.weekDay,
          dayOfMonth: schedule.monthDay,
          attachExcel: schedule.attachExcel,
          recipients: schedule.recipients.map(r => r.email) // Backend expects array of email strings
        };
        console.log('📤 Sending API request:', apiPayload);
        const res = await axios.post(`${API}/inventory-mail/schedules`, apiPayload);
        console.log('📥 API response:', res.data);

        if (res.data.success) {
          // Use the backend-returned data with database ID
          const newSchedule: ScheduleConfig = {
            ...schedule,
            id: res.data.data?.id?.toString() || schedule.id,
            nextRun: res.data.data?.nextRun || schedule.nextRun
          };
          setSchedules([...schedules, newSchedule]);
          showMessage('success', `✅ Schedule "${schedule.name}" created successfully!`);
          resetForm();
          setActiveTab('schedules');
          // Refresh to get accurate data from server
          fetchSchedules();
        } else {
          showMessage('error', `❌ Failed: ${res.data.error || 'Unknown error'}`);
        }
      }
    } catch (e: any) {
      console.error('❌ API error:', e);
      showMessage('error', `❌ Error creating schedule: ${e.response?.data?.error || e.message || 'Connection failed'}`);
    } finally {
      setIsCreating(false);
    }
  };

  const calculateNextRun = (frequency: string, time: string, weekDay?: number, monthDay?: number): string => {
    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    const next = new Date(now);
    next.setHours(hours, minutes, 0, 0);

    if (next <= now) {
      if (frequency === 'daily') {
        next.setDate(next.getDate() + 1);
      } else if (frequency === 'weekly') {
        next.setDate(next.getDate() + 7);
      } else {
        next.setMonth(next.getMonth() + 1);
      }
    }

    return next.toISOString();
  };

  const toggleScheduleActive = async (id: string) => {
    const schedule = schedules.find(s => s.id === id);
    if (!schedule) return;

    try {
      await axios.patch(`${API}/inventory-mail/schedules/${id}`, { isActive: !schedule.isActive });
    } catch (e) {
      // Continue anyway
    }

    setSchedules(schedules.map(s => 
      s.id === id ? { ...s, isActive: !s.isActive } : s
    ));
    showMessage('success', `Schedule ${!schedule.isActive ? 'activated' : 'paused'}`);
  };

  const deleteSchedule = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;

    try {
      await axios.delete(`${API}/inventory-mail/schedules/${id}`);
    } catch (e) {
      // Continue anyway
    }

    setSchedules(schedules.filter(s => s.id !== id));
    showMessage('success', '✅ Schedule deleted');
  };

  const sendTestEmail = async (schedule: ScheduleConfig) => {
    showMessage('info', '📧 Sending test email...');
    try {
      // Map frontend schedule to backend expected format
      const backendSchedule = {
        id: parseInt(schedule.id) || 0,
        name: schedule.name,
        type: schedule.type,
        target_id: schedule.targetId,
        target_name: schedule.targetName,
        frequency: schedule.frequency,
        time_of_day: schedule.time,
        day_of_week: schedule.weekDay,
        day_of_month: schedule.monthDay,
        attach_excel: schedule.attachExcel,
        recipients: schedule.recipients.map(r => r.email)
      };
      const res = await axios.post(`${API}/inventory-mail/send-test`, { schedule: backendSchedule });
      if (res.data.success) {
        showMessage('success', `📧 Test email sent to ${schedule.recipients.length} recipient(s)!`);
      } else {
        showMessage('error', `❌ Failed: ${res.data.error || 'Unknown error'}`);
      }
    } catch (e: any) {
      showMessage('error', `❌ Error: ${e.response?.data?.error || e.message || 'Connection failed'}`);
    }
  };

  const runNow = async (schedule: ScheduleConfig) => {
    showMessage('info', '🚀 Running schedule now...');
    try {
      const res = await axios.post(`${API}/inventory-mail/run-now`, {
        scheduleId: schedule.id
      });
      if (res.data.success) {
        showMessage('success', `✅ Report sent successfully!`);
        fetchEmailHistory();
        // Refresh schedules to get updated lastRun/nextRun
        fetchSchedules();
      } else {
        showMessage('error', `❌ Failed: ${res.data.error || 'Unknown error'}`);
      }
    } catch (e: any) {
      showMessage('error', `❌ Error: ${e.response?.data?.error || e.message || 'Connection failed'}`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'client',
      targetId: '',
      targetName: '',
      frequency: 'daily',
      time: '08:00',
      weekDay: 1,
      monthDay: 1,
      recipients: [],
      includeExpiry: true,
      includeStock: true,
      includePallets: true,
      includeBins: true,
      includeActivity: true,
      includeCharts: true,
      attachExcel: true,
      isActive: true,
      useGroup: false,
      groupId: ''
    });
    setNewRecipient({ email: '', name: '', role: 'primary' });
  };

  // ============ CUSTOMER GROUP HANDLERS ============
  const saveGroup = async () => {
    if (!groupForm.name.trim()) {
      showMessage('error', '⚠️ Please enter a group name');
      return;
    }
    if (groupForm.customers.length === 0) {
      showMessage('error', '⚠️ Please select at least one customer');
      return;
    }

    const group: CustomerGroup = {
      id: editingGroup?.id || Date.now().toString(),
      name: groupForm.name.trim(),
      color: groupForm.color,
      customers: groupForm.customers,
      createdAt: editingGroup?.createdAt || new Date().toISOString()
    };

    try {
      const res = await axios.post(`${API}/customer-groups`, group);
      if (res.data.success) {
        if (editingGroup) {
          setCustomerGroups(customerGroups.map(g => g.id === group.id ? group : g));
          showMessage('success', `✅ Group "${group.name}" updated successfully!`);
        } else {
          setCustomerGroups([...customerGroups, group]);
          showMessage('success', `✅ Group "${group.name}" created successfully!`);
        }
      }
    } catch (e) {
      // Fallback to localStorage
      if (editingGroup) {
        setCustomerGroups(customerGroups.map(g => g.id === group.id ? group : g));
      } else {
        setCustomerGroups([...customerGroups, group]);
      }
      const updated = editingGroup
        ? customerGroups.map(g => g.id === group.id ? group : g)
        : [...customerGroups, group];
      localStorage.setItem('customerGroups', JSON.stringify(updated));
      showMessage('success', `✅ Group "${group.name}" saved locally!`);
    }

    setShowGroupModal(false);
    setEditingGroup(null);
    setGroupForm({ name: '', color: '#3b82f6', customers: [] });
    setGroupSearchTerm('');
  };

  const deleteGroup = async (groupId: string) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;

    try {
      await axios.delete(`${API}/customer-groups/${groupId}`);
    } catch (e) {
      // Continue anyway
    }

    const updated = customerGroups.filter(g => g.id !== groupId);
    setCustomerGroups(updated);
    localStorage.setItem('customerGroups', JSON.stringify(updated));
    showMessage('success', '✅ Group deleted');
  };

  const openEditGroup = (group: CustomerGroup) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, color: group.color, customers: [...group.customers] });
    setShowGroupModal(true);
  };

  const toggleCustomerInGroup = (customerCode: string) => {
    if (groupForm.customers.includes(customerCode)) {
      setGroupForm({ ...groupForm, customers: groupForm.customers.filter(c => c !== customerCode) });
    } else {
      setGroupForm({ ...groupForm, customers: [...groupForm.customers, customerCode] });
    }
  };

  // ============ STYLES ============
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.95)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: 500,
    outline: 'none'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginBottom: '6px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const checkboxStyle: React.CSSProperties = {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#4facfe'
  };

  const cardStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(79, 172, 254, 0.2)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
  };

  // ============ RENDER FUNCTIONS ============
  const renderScheduleCard = (schedule: ScheduleConfig) => (
    <div
      className="ams-schedule-card"
      key={schedule.id}
      style={{
        ...cardStyle,
        border: `1px solid ${schedule.isActive ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.3)'}`,
        opacity: schedule.isActive ? 1 : 0.7
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ fontSize: '24px' }}>{schedule.type === 'group' ? '👥' : schedule.type === 'client' ? '👤' : '🏭'}</span>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#fff' }}>
              {schedule.name}
            </h3>
            <span style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 700,
              background: schedule.isActive ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
              color: schedule.isActive ? '#10b981' : '#ef4444'
            }}>
              {schedule.isActive ? '● ACTIVE' : '● PAUSED'}
            </span>
          </div>
          <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
            {schedule.type === 'group' ? 'Group' : schedule.type === 'client' ? 'Client' : 'Warehouse'}: <strong style={{ color: '#4facfe' }}>{schedule.targetName}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="ams-btn ams-btn-secondary"
            onClick={() => toggleScheduleActive(schedule.id)}
            style={{
              padding: '8px 14px',
              background: schedule.isActive ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
              border: 'none',
              borderRadius: '8px',
              color: schedule.isActive ? '#ef4444' : '#10b981',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {schedule.isActive ? '⏸ Pause' : '▶ Activate'}
          </button>
          <button
            className="ams-btn ams-btn-danger"
            onClick={() => deleteSchedule(schedule.id)}
            style={{
              padding: '8px 12px',
              background: 'rgba(239,68,68,0.2)',
              border: 'none',
              borderRadius: '8px',
              color: '#ef4444',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            🗑️
          </button>
        </div>
      </div>

      {/* Schedule Details */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '12px', 
        marginBottom: '16px',
        padding: '14px',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '10px'
      }}>
        <div>
          <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Frequency</div>
          <div style={{ fontSize: '14px', color: '#fff', fontWeight: 600 }}>
            {schedule.frequency === 'daily' && '📅 Daily'}
            {schedule.frequency === 'weekly' && `📅 Weekly (${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][schedule.weekDay || 0]})`}
            {schedule.frequency === 'monthly' && `📅 Monthly (Day ${schedule.monthDay})`}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Time</div>
          <div style={{ fontSize: '14px', color: '#fff', fontWeight: 600 }}>⏰ {schedule.time}</div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Recipients</div>
          <div style={{ fontSize: '14px', color: '#fff', fontWeight: 600 }}>📧 {schedule.recipients.length} email(s)</div>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Next Run</div>
          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
            {schedule.nextRun ? new Date(schedule.nextRun).toLocaleString() : 'N/A'}
          </div>
        </div>
      </div>

      {/* Included Sections */}
      <div style={{ marginBottom: '16px' }}>
        <div className="ams-section-title" style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Report Includes:</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {schedule.includeStock && <span style={{ padding: '4px 10px', background: 'rgba(59,130,246,0.2)', borderRadius: '6px', fontSize: '11px', color: '#3b82f6' }}>📦 Stock</span>}
          {schedule.includePallets && <span style={{ padding: '4px 10px', background: 'rgba(245,158,11,0.2)', borderRadius: '6px', fontSize: '11px', color: '#f59e0b' }}>📦 Pallets</span>}
          {schedule.includeBins && <span style={{ padding: '4px 10px', background: 'rgba(139,92,246,0.2)', borderRadius: '6px', fontSize: '11px', color: '#8b5cf6' }}>📍 Bins</span>}
          {schedule.includeExpiry && <span style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.2)', borderRadius: '6px', fontSize: '11px', color: '#ef4444' }}>⏰ Expiry</span>}
          {schedule.includeActivity && <span style={{ padding: '4px 10px', background: 'rgba(16,185,129,0.2)', borderRadius: '6px', fontSize: '11px', color: '#10b981' }}>📊 Activity</span>}
          {schedule.includeCharts && <span style={{ padding: '4px 10px', background: 'rgba(236,72,153,0.2)', borderRadius: '6px', fontSize: '11px', color: '#ec4899' }}>📈 Charts</span>}
          {schedule.attachExcel && <span style={{ padding: '4px 10px', background: 'rgba(34,197,94,0.2)', borderRadius: '6px', fontSize: '11px', color: '#22c55e' }}>📎 Excel</span>}
        </div>
      </div>

      {/* Recipients */}
      <div style={{ marginBottom: '16px' }}>
        <div className="ams-section-title" style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Recipients:</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {schedule.recipients.map(r => (
            <span 
              key={r.id} 
              style={{ 
                padding: '6px 12px', 
                background: r.role === 'primary' ? 'rgba(79,172,254,0.15)' : 'rgba(148,163,184,0.15)', 
                borderRadius: '8px', 
                fontSize: '12px', 
                color: r.role === 'primary' ? '#4facfe' : '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {r.role === 'primary' ? '👤' : r.role === 'cc' ? '📋' : '👁️'}
              {r.name} ({r.email})
            </span>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '10px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          className="ams-btn ams-btn-primary"
          onClick={() => sendTestEmail(schedule)}
          style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #4facfe, #00f2fe)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📧 Send Test Email
        </button>
        <button
          className="ams-btn ams-btn-primary"
          onClick={() => runNow(schedule)}
          disabled={!schedule.isActive}
          style={{
            flex: 1,
            padding: '12px',
            background: schedule.isActive
              ? 'linear-gradient(135deg, #10b981, #059669)'
              : 'rgba(148,163,184,0.3)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 700,
            cursor: schedule.isActive ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: schedule.isActive ? 1 : 0.5
          }}
        >
          🚀 Run Now
        </button>
        <button
          className="ams-btn ams-btn-primary"
          onClick={() => { setSelectedSchedule(schedule); setActiveTab('preview'); }}
          style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👁️ Preview Email
        </button>
      </div>
    </div>
  );

  const renderCreateForm = () => (
    <div className="ams-card" style={cardStyle}>
      <h3 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '28px' }}>➕</span>
        Create New Email Schedule
      </h3>

      {/* Basic Info */}
      <div style={{ marginBottom: '24px' }}>
        <h4 className="ams-section-title" style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 700, color: '#4facfe', textTransform: 'uppercase' }}>
          📋 Basic Information
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Schedule Name *</label>
            <input
              className="ams-input"
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Daily Client Report"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Report Type *</label>
            <select
              className="ams-select"
              value={formData.type}
              onChange={e => setFormData({ ...formData, type: e.target.value as 'client' | 'warehouse', targetId: '' })}
              style={inputStyle}
            >
              <option value="client">👤 Client Level</option>
              <option value="warehouse">🏭 Warehouse Level</option>
            </select>
          </div>
          <div>
            {formData.type === 'client' && customerGroups.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.useGroup}
                    onChange={e => setFormData({ ...formData, useGroup: e.target.checked, targetId: '', groupId: '' })}
                    style={checkboxStyle}
                  />
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>Use Customer Group</span>
                </label>
              </div>
            )}
            <label style={labelStyle}>
              {formData.useGroup ? 'Select Customer Group *' : (formData.type === 'client' ? 'Select Client *' : 'Select Warehouse *')}
            </label>
            {formData.useGroup ? (
              <select
                className="ams-select"
                value={formData.groupId}
                onChange={e => {
                  const group = customerGroups.find(g => g.id === e.target.value);
                  setFormData({ ...formData, groupId: e.target.value, targetId: group?.customers[0] || '', targetName: group?.name || '' });
                }}
                style={inputStyle}
              >
                <option value="">Select Group...</option>
                {customerGroups.map(g => (
                  <option key={g.id} value={g.id}>👥 {g.name} ({g.customers.length} customers)</option>
                ))}
              </select>
            ) : (
              <select
                className="ams-select"
                value={formData.targetId}
                onChange={e => setFormData({ ...formData, targetId: e.target.value })}
                style={inputStyle}
              >
                <option value="">
                  {formData.type === 'client'
                    ? (customerList.length === 0 ? '⚠️ Load data from Customer Info tab first' : 'Select Client...')
                    : (warehouseList.length === 0 ? '⚠️ Load data first' : 'Select Warehouse...')
                  }
                </option>
                {formData.type === 'client'
                  ? customerList.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)
                  : warehouseList.map(w => <option key={w} value={w}>{w}</option>)
                }
              </select>
            )}
            {formData.useGroup && formData.groupId && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                Will create schedules for: {customerGroups.find(g => g.id === formData.groupId)?.customers.slice(0, 5).join(', ')}
                {(customerGroups.find(g => g.id === formData.groupId)?.customers.length || 0) > 5 && '...'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Schedule Timing */}
      <div style={{ marginBottom: '24px' }}>
        <h4 className="ams-section-title" style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' }}>
          ⏰ Schedule Timing
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Frequency *</label>
            <select
              className="ams-select"
              value={formData.frequency}
              onChange={e => setFormData({ ...formData, frequency: e.target.value as 'daily' | 'weekly' | 'monthly' })}
              style={inputStyle}
            >
              <option value="daily">📅 Daily</option>
              <option value="weekly">📅 Weekly</option>
              <option value="monthly">📅 Monthly</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Time (24h) *</label>
            <input
              className="ams-input"
              type="time"
              value={formData.time}
              onChange={e => setFormData({ ...formData, time: e.target.value })}
              style={inputStyle}
            />
          </div>
          {formData.frequency === 'weekly' && (
            <div>
              <label style={labelStyle}>Day of Week</label>
              <select
                className="ams-select"
                value={formData.weekDay}
                onChange={e => setFormData({ ...formData, weekDay: parseInt(e.target.value) })}
                style={inputStyle}
              >
                <option value={0}>Sunday</option>
                <option value={1}>Monday</option>
                <option value={2}>Tuesday</option>
                <option value={3}>Wednesday</option>
                <option value={4}>Thursday</option>
                <option value={5}>Friday</option>
                <option value={6}>Saturday</option>
              </select>
            </div>
          )}
          {formData.frequency === 'monthly' && (
            <div>
              <label style={labelStyle}>Day of Month</label>
              <select
                className="ams-select"
                value={formData.monthDay}
                onChange={e => setFormData({ ...formData, monthDay: parseInt(e.target.value) })}
                style={inputStyle}
              >
                {Array.from({ length: 28 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Recipients */}
      <div style={{ marginBottom: '24px' }}>
        <h4 className="ams-section-title" style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 700, color: '#10b981', textTransform: 'uppercase' }}>
          📧 Recipients (Max 5)
        </h4>
        
        {/* Add Recipient Form */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 120px auto', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Name</label>
            <input
              className="ams-input"
              type="text"
              value={newRecipient.name}
              onChange={e => setNewRecipient({ ...newRecipient, name: e.target.value })}
              placeholder="John Doe"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input
              className="ams-input"
              type="email"
              value={newRecipient.email}
              onChange={e => setNewRecipient({ ...newRecipient, email: e.target.value })}
              placeholder="john@company.com"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Role</label>
            <select
              className="ams-select"
              value={newRecipient.role}
              onChange={e => setNewRecipient({ ...newRecipient, role: e.target.value as 'primary' | 'cc' | 'bcc' })}
              style={inputStyle}
            >
              <option value="primary">Primary (To)</option>
              <option value="cc">CC</option>
              <option value="bcc">BCC</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              className="ams-btn ams-btn-primary"
              onClick={addRecipient}
              disabled={(formData.recipients?.length || 0) >= 5}
              style={{
                padding: '12px 20px',
                background: (formData.recipients?.length || 0) >= 5
                  ? 'rgba(148,163,184,0.3)'
                  : 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
                cursor: (formData.recipients?.length || 0) >= 5 ? 'not-allowed' : 'pointer'
              }}
            >
              + Add
            </button>
          </div>
        </div>

        {/* Recipients List */}
        {formData.recipients && formData.recipients.length > 0 && (
          <div style={{ 
            background: 'rgba(0,0,0,0.2)', 
            borderRadius: '10px', 
            padding: '12px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            {formData.recipients.map(r => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  background: 'rgba(79,172,254,0.15)',
                  borderRadius: '8px',
                  border: '1px solid rgba(79,172,254,0.3)'
                }}
              >
                <span style={{ color: '#4facfe', fontSize: '13px' }}>
                  {r.role === 'primary' ? '👤' : r.role === 'cc' ? '📋' : '👁️'}
                </span>
                <span style={{ color: '#fff', fontSize: '13px', fontWeight: 500 }}>{r.name}</span>
                <span style={{ color: '#64748b', fontSize: '11px' }}>({r.email})</span>
                <span style={{ 
                  fontSize: '10px', 
                  padding: '2px 6px', 
                  background: 'rgba(255,255,255,0.1)', 
                  borderRadius: '4px',
                  color: '#94a3b8'
                }}>
                  {r.role.toUpperCase()}
                </span>
                <button
                  onClick={() => removeRecipient(r.id)}
                  style={{
                    background: 'rgba(239,68,68,0.2)',
                    border: 'none',
                    borderRadius: '4px',
                    color: '#ef4444',
                    padding: '4px 6px',
                    cursor: 'pointer',
                    fontSize: '10px'
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report Content */}
      <div style={{ marginBottom: '24px' }}>
        <h4 className="ams-section-title" style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase' }}>
          📊 Report Content
        </h4>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '12px',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '12px',
          padding: '16px'
        }}>
          {[
            { key: 'includeStock', label: 'Stock Levels', icon: '📦', color: '#3b82f6' },
            { key: 'includePallets', label: 'Pallet Info', icon: '📦', color: '#f59e0b' },
            { key: 'includeBins', label: 'Bin Locations', icon: '📍', color: '#8b5cf6' },
            { key: 'includeExpiry', label: 'Expiry Data', icon: '⏰', color: '#ef4444' },
            { key: 'includeActivity', label: 'Activity Log', icon: '📊', color: '#10b981' },
            { key: 'includeCharts', label: 'Charts/Graphs', icon: '📈', color: '#ec4899' },
            { key: 'attachExcel', label: 'Excel Attachment', icon: '📎', color: '#22c55e' },
          ].map(item => (
            <label
              key={item.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px',
                background: (formData as any)[item.key] ? `rgba(${item.color === '#3b82f6' ? '59,130,246' : item.color === '#f59e0b' ? '245,158,11' : item.color === '#8b5cf6' ? '139,92,246' : item.color === '#ef4444' ? '239,68,68' : item.color === '#10b981' ? '16,185,129' : item.color === '#ec4899' ? '236,72,153' : '34,197,94'},0.15)` : 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                cursor: 'pointer',
                border: `1px solid ${(formData as any)[item.key] ? item.color : 'transparent'}`,
                transition: 'all 0.2s'
              }}
            >
              <input
                type="checkbox"
                checked={(formData as any)[item.key]}
                onChange={e => setFormData({ ...formData, [item.key]: e.target.checked })}
                style={checkboxStyle}
              />
              <span style={{ fontSize: '18px' }}>{item.icon}</span>
              <span style={{ fontSize: '13px', color: (formData as any)[item.key] ? '#fff' : '#94a3b8', fontWeight: 500 }}>
                {item.label}
              </span>
            </label>
          ))}
        </div>

        {/* Excel Template Link */}
        {formData.attachExcel && (
          <div style={{
            marginTop: '12px',
            padding: '14px 16px',
            background: 'linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(16,185,129,0.08) 100%)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>📊</span>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#22c55e' }}>Excel Column Template</div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Choose which columns appear in the Excel attachment</div>
              </div>
            </div>
            <button
              className="ams-btn ams-btn-secondary"
              onClick={() => setActiveTab('templates')}
              style={{
                padding: '8px 16px',
                background: 'rgba(34,197,94,0.2)',
                border: '1px solid rgba(34,197,94,0.4)',
                borderRadius: '8px',
                color: '#22c55e',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Customize Columns →
            </button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          className="ams-btn ams-btn-primary"
          onClick={createSchedule}
          disabled={isCreating}
          style={{
            flex: 1,
            padding: '14px',
            background: isCreating ? 'rgba(148,163,184,0.5)' : 'linear-gradient(135deg, #4facfe, #00f2fe)',
            border: 'none',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: 700,
            cursor: isCreating ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            opacity: isCreating ? 0.7 : 1
          }}
        >
          {isCreating ? '⏳ Creating...' : '✅ Create Schedule'}
        </button>
        <button
          className="ams-btn ams-btn-secondary"
          onClick={resetForm}
          style={{
            padding: '14px 28px',
            background: 'rgba(148,163,184,0.2)',
            border: 'none',
            borderRadius: '10px',
            color: '#94a3b8',
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer'
          }}
        >
          🔄 Reset
        </button>
      </div>
    </div>
  );

  const renderEmailPreview = () => {
    const schedule = selectedSchedule || schedules[0];
    if (!schedule) return <div style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>No schedule selected</div>;

    return (
      <div className="ams-card" style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fff' }}>
            👁️ Email Preview: {schedule.name}
          </h3>
          <button
            className="ams-btn ams-btn-secondary"
            onClick={() => setActiveTab('schedules')}
            style={{
              padding: '8px 16px',
              background: 'rgba(148,163,184,0.2)',
              border: 'none',
              borderRadius: '8px',
              color: '#94a3b8',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
        </div>

        {/* Email Preview */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
        }}>
          {/* Email Header */}
          <div style={{ background: '#f8f9fa', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
              <strong>From:</strong> HALA Warehouse System &lt;noreply@hala-warehouse.com&gt;
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
              <strong>To:</strong> {schedule.recipients.filter(r => r.role === 'primary').map(r => `${r.name} <${r.email}>`).join(', ')}
            </div>
            {schedule.recipients.filter(r => r.role === 'cc').length > 0 && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                <strong>CC:</strong> {schedule.recipients.filter(r => r.role === 'cc').map(r => `${r.name} <${r.email}>`).join(', ')}
              </div>
            )}
            <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 600 }}>
              <strong>Subject:</strong> 📊 {schedule.type === 'group' ? 'Group' : schedule.type === 'client' ? 'Client' : 'Warehouse'} Inventory Report - {schedule.targetName} - {new Date().toLocaleDateString()}
            </div>
          </div>

          {/* Email Body */}
          <div style={{ padding: '24px' }}>
            {/* Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              color: '#fff',
              textAlign: 'center'
            }}>
              <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: 800 }}>📦 HALA Warehouse</h1>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, opacity: 0.9 }}>
                Inventory Report - {schedule.targetName}
              </h2>
              <p style={{ margin: '12px 0 0', fontSize: '14px', opacity: 0.8 }}>
                Generated: {new Date().toLocaleString()}
              </p>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
              {schedule.includeStock && (
                <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>📦</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e40af' }}>{stats?.TOTAL_SEGMENTS?.toLocaleString() || '156,432'}</div>
                  <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 600 }}>Total Stock</div>
                </div>
              )}
              {schedule.includePallets && (
                <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #fcd34d' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>📦</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#92400e' }}>{stats?.TOTAL_BINS?.toLocaleString() || '8,234'}</div>
                  <div style={{ fontSize: '12px', color: '#d97706', fontWeight: 600 }}>Total Pallets</div>
                </div>
              )}
              {schedule.includeBins && (
                <div style={{ background: '#f3e8ff', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #c4b5fd' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>📍</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#6b21a8' }}>{stats?.TOTAL_STORAGE_AREAS?.toLocaleString() || '342'}</div>
                  <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: 600 }}>Bin Locations</div>
                </div>
              )}
              {schedule.includeExpiry && (
                <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #fecaca' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>⚠️</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#991b1b' }}>{expiryStats?.EXPIRING_SOON || '23'}</div>
                  <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>Expiring Soon</div>
                </div>
              )}
            </div>

            {/* Utilization Section */}
            <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '20px', marginBottom: '24px', border: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: '#374151' }}>📊 Storage Utilization</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Physical Capacity</div>
                  <div style={{ 
                    height: '24px', 
                    background: '#e5e7eb', 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      width: `${stats?.UTILIZED_PCT || 72}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #10b981, #059669)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>{stats?.UTILIZED_PCT || 72}%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Virtual Locations</div>
                  <div style={{ 
                    height: '24px', 
                    background: '#e5e7eb', 
                    borderRadius: '12px', 
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${virtualStats?.UTILIZED_PCT || 45}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>{virtualStats?.UTILIZED_PCT || 45}%</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Available Space</div>
                  <div style={{ 
                    height: '24px', 
                    background: '#e5e7eb', 
                    borderRadius: '12px', 
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${stats?.FREE_PCT || 28}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>{stats?.FREE_PCT || 28}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Table Preview */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: '#374151' }}>📋 Top Storage Areas</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 700 }}>Area</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 700 }}>Bins</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 700 }}>Capacity</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 700 }}>Used</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: 700 }}>Utilization</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { area: 'AREA-A1', bins: 234, capacity: 4680, used: 3744, util: 80 },
                    { area: 'AREA-B2', bins: 189, capacity: 3780, used: 2835, util: 75 },
                    { area: 'AREA-C3', bins: 156, capacity: 3120, used: 2184, util: 70 },
                    { area: 'AREA-D4', bins: 145, capacity: 2900, used: 1885, util: 65 },
                    { area: 'AREA-E5', bins: 128, capacity: 2560, used: 1536, util: 60 }
                  ].map((row, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#1f2937' }}>{row.area}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center', color: '#4b5563' }}>{row.bins.toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center', color: '#4b5563' }}>{row.capacity.toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center', color: '#4b5563' }}>{row.used.toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 700,
                          background: row.util >= 80 ? '#fef2f2' : row.util >= 60 ? '#fef3c7' : '#ecfdf5',
                          color: row.util >= 80 ? '#dc2626' : row.util >= 60 ? '#d97706' : '#059669'
                        }}>
                          {row.util}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Expiry Alert Section */}
            {schedule.includeExpiry && (
              <div style={{ 
                background: '#fef2f2', 
                borderRadius: '10px', 
                padding: '20px', 
                marginBottom: '24px',
                border: '1px solid #fecaca'
              }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 700, color: '#991b1b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  ⚠️ Expiry Alerts
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#dc2626' }}>12</div>
                    <div style={{ fontSize: '11px', color: '#991b1b' }}>Expired Items</div>
                  </div>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#ea580c' }}>23</div>
                    <div style={{ fontSize: '11px', color: '#c2410c' }}>Expiring in 30 Days</div>
                  </div>
                  <div style={{ background: '#fff', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#d97706' }}>45</div>
                    <div style={{ fontSize: '11px', color: '#b45309' }}>Expiring in 90 Days</div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={{ 
              textAlign: 'center', 
              padding: '20px', 
              background: '#f9fafb', 
              borderRadius: '10px',
              marginTop: '24px'
            }}>
              <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#6b7280' }}>
                This is an automated report from HALA Warehouse Management System
              </p>
              <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af' }}>
                © 2024 HALA Supply Chain Services • Riyadh, Saudi Arabia
              </p>
              {schedule.attachExcel && (
                <div style={{ marginTop: '16px', padding: '12px', background: '#ecfdf5', borderRadius: '8px', display: 'inline-block' }}>
                  <span style={{ fontSize: '13px', color: '#059669', fontWeight: 600 }}>
                    📎 Excel attachment: inventory_report_{new Date().toISOString().split('T')[0]}.xlsx
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGroups = () => {
    const filteredCustomers = customerList.filter(c =>
      c.code.toLowerCase().includes(groupSearchTerm.toLowerCase()) ||
      c.name.toLowerCase().includes(groupSearchTerm.toLowerCase())
    );

    return (
      <div className="ams-card" style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '28px' }}>👥</span>
            Customer Groups
          </h3>
          <button
            className="ams-btn ams-btn-primary"
            onClick={() => { setEditingGroup(null); setGroupForm({ name: '', color: '#3b82f6', customers: [] }); setShowGroupModal(true); }}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ➕ New Group
          </button>
        </div>

        {customerGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
            <p>No customer groups yet. Create groups to schedule reports for multiple customers at once.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {customerGroups.map(group => (
              <div
                key={group.id}
                style={{
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `2px solid ${group.color}40`,
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: group.color
                  }}></div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#fff', flex: 1 }}>{group.name}</h4>
                  <button
                    className="ams-btn ams-btn-secondary"
                    onClick={() => openEditGroup(group)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(79,172,254,0.2)',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#4facfe',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="ams-btn ams-btn-danger"
                    onClick={() => deleteGroup(group.id)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(239,68,68,0.2)',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#ef4444',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️
                  </button>
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                  {group.customers.length} customer{group.customers.length !== 1 ? 's' : ''}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {group.customers.slice(0, 8).map(code => {
                    const cust = customerList.find(c => c.code === code);
                    return (
                      <span
                        key={code}
                        style={{
                          padding: '4px 10px',
                          background: `${group.color}20`,
                          borderRadius: '6px',
                          fontSize: '11px',
                          color: group.color,
                          fontWeight: 600
                        }}
                      >
                        {code}
                      </span>
                    );
                  })}
                  {group.customers.length > 8 && (
                    <span style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', fontSize: '11px', color: '#94a3b8' }}>
                      +{group.customers.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Group Modal */}
        {showGroupModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              borderRadius: '16px',
              padding: '28px',
              width: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              border: '1px solid rgba(79,172,254,0.3)'
            }}>
              <h3 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                {editingGroup ? '✏️ Edit Group' : '➕ New Customer Group'}
              </h3>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Group Name *</label>
                <input
                  className="ams-input"
                  type="text"
                  value={groupForm.name}
                  onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
                  placeholder="e.g., Premium Clients"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Group Color</label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'].map(color => (
                    <button
                      key={color}
                      onClick={() => setGroupForm({ ...groupForm, color })}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: color,
                        border: groupForm.color === color ? '3px solid #fff' : '3px solid transparent',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        transform: groupForm.color === color ? 'scale(1.1)' : 'scale(1)'
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Select Customers ({groupForm.customers.length} selected)</label>
                <input
                  className="ams-input"
                  type="text"
                  value={groupSearchTerm}
                  onChange={e => setGroupSearchTerm(e.target.value)}
                  placeholder="Search customers..."
                  style={{ ...inputStyle, marginBottom: '12px' }}
                />
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '10px',
                  padding: '10px'
                }}>
                  {filteredCustomers.length === 0 ? (
                    <div style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>
                      No customers found
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {filteredCustomers.map(c => (
                        <label
                          key={c.code}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 12px',
                            background: groupForm.customers.includes(c.code) ? `${groupForm.color}20` : 'rgba(255,255,255,0.05)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: groupForm.customers.includes(c.code) ? `1px solid ${groupForm.color}` : '1px solid transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={groupForm.customers.includes(c.code)}
                            onChange={() => toggleCustomerInGroup(c.code)}
                            style={checkboxStyle}
                          />
                          <div>
                            <div style={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>{c.code}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{c.name}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button
                  className="ams-btn ams-btn-primary"
                  onClick={saveGroup}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '15px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {editingGroup ? '💾 Save Changes' : '✅ Create Group'}
                </button>
                <button
                  className="ams-btn ams-btn-secondary"
                  onClick={() => { setShowGroupModal(false); setEditingGroup(null); setGroupSearchTerm(''); }}
                  style={{
                    padding: '14px 28px',
                    background: 'rgba(148,163,184,0.2)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#94a3b8',
                    fontSize: '15px',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHistory = () => (
    <div className="ams-card" style={cardStyle}>
      <h3 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '28px' }}>📜</span>
        Email History
      </h3>

      {emailHistory.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <p>No emails sent yet. Create a schedule and run it to see history here.</p>
        </div>
      ) : (
        <table className="ams-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.1)' }}>
              <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontWeight: 700 }}>Date/Time</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontWeight: 700 }}>Schedule</th>
              <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontWeight: 700 }}>Recipients</th>
              <th style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontWeight: 700 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {emailHistory.map((item, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '12px', color: '#e2e8f0' }}>{new Date(item.sentAt).toLocaleString()}</td>
                <td style={{ padding: '12px', color: '#4facfe', fontWeight: 600 }}>{item.scheduleName}</td>
                <td style={{ padding: '12px', color: '#94a3b8' }}>{item.recipients}</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 700,
                    background: item.status === 'sent' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                    color: item.status === 'sent' ? '#10b981' : '#ef4444'
                  }}>
                    {item.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // ============ MAIN RENDER ============
  if (isLoading) {
    return (
      <div style={{
        width: '100%',
        height: 'calc(100vh - 180px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        color: '#4facfe',
        fontSize: '18px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div>Loading email schedules...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ams-container">
      {/* Header */}
      <div className="ams-header">
        <div>
          <h2 className="ams-title">📧 Inventory Auto-Mail Scheduler</h2>
          <p className="ams-subtitle">
            Schedule automated inventory reports • <strong className="ams-text-accent">{schedules.length} schedules</strong> configured
            • <strong className="ams-text-success">{schedules.filter(s => s.isActive).length} active</strong>
          </p>
        </div>
        <div className="ams-header-actions">
          <button className="ams-btn ams-btn-primary" onClick={fetchSchedules}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`ams-message ams-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="ams-tabs">
        {[
          { key: 'schedules', label: '📋 Schedules', count: schedules.length },
          { key: 'create', label: '➕ Create New', count: null },
          { key: 'templates', label: '📊 Excel Templates', count: null },
          { key: 'groups', label: '👥 Customer Groups', count: customerGroups.length },
          { key: 'preview', label: '👁️ Preview', count: null },
          { key: 'history', label: '📜 History', count: emailHistory.length }
        ].map(tab => (
          <button
            className={`ams-tab ${activeTab === tab.key ? 'ams-tab-active' : ''}`}
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
          >
            {tab.label}
            {tab.count !== null && <span className="ams-tab-badge">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Content Panels */}
      {activeTab === 'schedules' && (
        <div className="ams-content-panel">
          {schedules.length === 0 ? (
            <div className="ams-empty-state">
              <div className="ams-empty-icon">📧</div>
              <h3 className="ams-empty-title">No Schedules Yet</h3>
              <p className="ams-empty-text">
                Create your first automated email schedule to start receiving inventory reports
              </p>
              <button className="ams-btn ams-btn-primary" onClick={() => setActiveTab('create')}>
                ➕ Create First Schedule
              </button>
            </div>
          ) : (
            <div className="ams-schedule-grid">
              {schedules.map(renderScheduleCard)}
            </div>
          )}
        </div>
      )}

      {activeTab === 'create' && renderCreateForm()}
      {activeTab === 'groups' && renderGroups()}
      {activeTab === 'preview' && renderEmailPreview()}
      {activeTab === 'history' && renderHistory()}

      {/* Status Badge */}
      <div className="ams-status-badge">
        <span className="ams-status-indicator"></span>
        Auto-Mail System Active • {schedules.filter(s => s.isActive).length} schedules running
      </div>

      {/* Excel Template Builder Overlay */}
      {activeTab === 'templates' && (
        <div className="ams-template-overlay">
          <ExcelTemplateBuilder
            customerList={customerList}
            mode="embedded"
            onClose={() => setActiveTab('schedules')}
            onSave={(template: any) => {
              showMessage('success', `✅ Template "${template.name}" saved!`);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default InventoryAutoMailScheduler;